import { QuestionCircleOutlined } from "@ant-design/icons";
import { Form, Input, InputNumber, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import { TableFormList } from "components/formList";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import { useFetchCommunicationMethodsQuery } from "features/communicationMethodsTable/communicationMethodsAPI";
import { useCommunicationMethodPermissions } from "features/communicationMethodsTable/hooks/useCommunicationMethodPermissions";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useState } from "react";
import styled from "styled-components";

const StyledForm = styled(Form)`
  max-width: 400px;
  padding-top: 8px;
`;

const filterOption = (inputValue, option) => {
  const fullOptionText = option.label;
  return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
};

const StyledSelect = styled(Select)`
  width: 200px;
`;

export default function PhoneOptionsModal({
  open,
  onSubmit,
  onCancel,
  title,
  provider,
  operationType,
}) {
  const { data: communicationMethods = [] } = useFetchCommunicationMethodsQuery(
    {
      provider,
    },
    {
      skip: !provider,
    },
  );
  const { filterAllowedCommunicationMethods } = useCommunicationMethodPermissions();
  const { data: agencySettings } = useFetchAgencyQuery();
  const { data: constants = {} } = useFetchBackendConstantsQuery();
  const [dataSource, setDataSource] = useState([]);
  const [maxPhoneNumbersToSend, setMaxPhoneNumbersToSend] = useState(3);
  const [phonePrioritizationStrategy, setPhonePrioritizationStrategy] = useState("");
  const [restrictToRightPartyPhonesStrategy, setRestrictToRightPartyPhonesStrategy] = useState("");
  const [campaignFilename, setCampaignFilename] = useState("");
  const [form] = Form.useForm();

  const onDelete = (record) => {
    setDataSource((prev) => {
      return prev.filter((d) => {
        return d.key !== record.key;
      });
    });
  };

  const onSave = ({ index, ...record }) => {
    if (index === -1) {
      setDataSource((prev) => [...prev, { ...record, key: Math.random() }]);
    } else {
      setDataSource((prev) => {
        const copy = [...prev];
        copy[index] = record;
        return copy;
      });
    }
    return { data: true };
  };

  const columns = [
    {
      dataIndex: "type",
      editable: true,
      inputType: "select",
      title: "Phone Type",
      options: (constants.phoneTypes ?? []).map(({ display, value }) => ({
        value,
        label: display,
      })),
      rules: [
        {
          required: true,
          message: "Select a phone type",
        },
      ],
      render: (text, record) => {
        return (constants.phoneTypes ?? []).find((type) => type.value === record.type)?.display;
      },
    },
    {
      dataIndex: "includeStatuses",
      editable: true,
      inputType: "select",
      options: (constants.phoneStatuses ?? []).map((status) => ({
        label: status.display,
        value: status.value,
      })),
      title: "Include Phone Statuses",
      inputProps: {
        allowClear: true,
        mode: "multiple",
      },
      render: (text, record) => {
        return (constants.phoneStatuses ?? [])
          .filter((status) => (record.includeStatuses ?? []).includes(status.value))
          .map((status) => <Tag key={status.value}>{status.display}</Tag>);
      },
    },
    {
      dataIndex: "excludeStatuses",
      editable: true,
      inputType: "select",
      options: (constants.phoneStatuses ?? []).map((status) => ({
        label: status.display,
        value: status.value,
      })),
      title: "Exclude Phone Statuses",
      inputProps: {
        allowClear: true,
        mode: "multiple",
      },
      render: (text, record) => {
        return (constants.phoneStatuses ?? [])
          .filter((status) => (record.excludeStatuses ?? []).includes(status.value))
          .map((status) => <Tag key={status.value}>{status.display}</Tag>);
      },
    },
    {
      dataIndex: "dataSources",
      editable: true,
      inputType: "select",
      options: constants.editableDataSourceTypes?.map(({ display, value }) => ({
        value,
        label: display,
      })),
      title: "Data Sources",
      inputProps: {
        allowClear: true,
        mode: "multiple",
      },
      render: (text, record) => {
        return (constants.editableDataSourceTypes ?? [])
          .filter((dataSourceType) => (record.dataSources ?? []).includes(dataSourceType.value))
          .map((dataSourceType) => <Tag key={dataSourceType.value}>{dataSourceType.display}</Tag>);
      },
    },
  ];

  // Add TransUnion columns  // Add NeuStar columns
  const neuStarColumns = [
    {
      dataIndex: "neuStarScoreStart",
      editable: true,
      inputType: "number",
      title: "NeuStar Score Start",
      render: (text) => text,
    },
    {
      dataIndex: "neuStarScoreEnd",
      editable: true,
      inputType: "number",
      title: "NeuStar Score End",
      render: (text) => text,
    },
  ];
  // @ts-ignore
  columns.push(...neuStarColumns);

  if (agencySettings?.isTransUnionEnabled) {
    // NOTE: TransUnion acquired TLO in 2013, and are two different products.
    const transUnionColumns = [
      {
        dataIndex: "transUnionScoreStart",
        editable: true,
        inputType: "number",
        title: "TransUnion Score Start",
        render: (text) => text,
      },
      {
        dataIndex: "transUnionScoreEnd",
        editable: true,
        inputType: "number",
        title: "TransUnion Score End",
        render: (text) => text,
      },
      {
        dataIndex: "tloScoreStart",
        editable: true,
        inputType: "number",
        title: "TLO Score Start",
        render: (text) => text,
      },
      {
        dataIndex: "tloScoreEnd",
        editable: true,
        inputType: "number",
        title: "TLO Score End",
        render: (text) => text,
      },
    ];
    // @ts-ignore
    columns.push(...transUnionColumns);
  }

  const communicationMethodOptions = filterAllowedCommunicationMethods(communicationMethods)
    .filter((communicationMethod) => {
      return communicationMethod.provider === provider && !communicationMethod.isArchived;
    })
    .map(({ id, name }) => ({
      value: id,
      label: name,
    }));

  const onOk = async () => {
    const values = await form.validateFields();
    onSubmit({
      ...values,
      phoneConfigs: dataSource,
      maxPhoneNumbersToSend,
      phonePrioritizationStrategy,
      campaignFilename,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      onOk={onOk}
      width={900}
      title={title}
    >
      {operationType === "sendText" && (
        <StyledForm labelAlign="left" layout="vertical" form={form}>
          <Form.Item
            label="Communication Method"
            name="template"
            rules={[
              {
                required: true,
                message: "Please select a communication method",
              },
            ]}
          >
            <Select
              showSearch
              filterOption={filterOption}
              popupMatchSelectWidth={false}
              options={communicationMethodOptions}
              placeholder="Select one..."
              allowClear
            />
          </Form.Item>
        </StyledForm>
      )}
      {operationType === "sendDialerCampaign" && (
        <>
          <h5>Phone Configuration</h5>
          <TableFormList data={dataSource} columns={columns} onSave={onSave} onDelete={onDelete} />
          <Space direction="vertical">
            {provider === "noetica" && (
              <Space direction="vertical">
                <Typography.Text strong>Campaign Filename</Typography.Text>
                <Input
                  value={campaignFilename}
                  onChange={(e) => setCampaignFilename(e.target.value)}
                />
              </Space>
            )}
            <Space direction="vertical">
              <Tooltip
                title="If 'None' is selected, all phones for each debtor will be used. If 'Only First RPC Phone' is selected, if the debtor has multiple RPC phones then only the first RPC phone will be used, else all phones will be used. If 'All RPC Phones' is selected, only the RPC phones for each debtor will be used if there are RPC phones, else all phones will be used."
                placement="right"
              >
                <Typography.Text strong>Right Party Contacts Strategy</Typography.Text>
                &nbsp;
                <QuestionCircleOutlined />
              </Tooltip>
              <div>
                <StyledSelect
                  options={[
                    { label: "None", value: "" },
                    {
                      label: "Only First RPC Phone",
                      value: "only_first_phone",
                    },
                    {
                      label: "All RPC Phones",
                      value: "all_rpc_phones",
                    },
                  ]}
                  placeholder="Select one..."
                  allowClear
                  popupMatchSelectWidth={false}
                  value={restrictToRightPartyPhonesStrategy}
                  onChange={(value) => {
                    setRestrictToRightPartyPhonesStrategy(value);
                  }}
                />
              </div>
            </Space>
            <Space direction="vertical">
              <Typography.Text strong>Max # of Phone Numbers to Send</Typography.Text>
              <div>
                <InputNumber
                  min={1}
                  max={10}
                  value={maxPhoneNumbersToSend}
                  onChange={(value) => {
                    setMaxPhoneNumbersToSend(value);
                  }}
                />
              </div>
            </Space>
            <Space direction="vertical">
              <Typography.Text strong>Priority</Typography.Text>
              <div>
                <StyledSelect
                  options={[
                    { label: "None", value: "" },
                    { label: "Least Dialed", value: "least_dialed" },
                  ]}
                  placeholder="Select one..."
                  allowClear
                  popupMatchSelectWidth={false}
                  value={phonePrioritizationStrategy}
                  onChange={(value) => {
                    setPhonePrioritizationStrategy(value);
                  }}
                />
              </div>
            </Space>
          </Space>
        </>
      )}
    </Modal>
  );
}
